import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileText, Sparkles, Search, MoreVertical, Trash2, Plus, ListFilter, X } from 'lucide-react';
import * as TestModule from 'modules/test';
import { isMarketplaceTest } from 'modules/test/marketplace-policy';
import * as AuthModule from 'modules/auth';
import { useOrgStore } from 'store/organization.store';
import { PERMISSIONS } from 'modules/auth/permissions';
import { useSearchParamState } from 'hooks/use-search-param-state';
import { useTestCategoryLabel } from 'hooks/use-test-category-label';
import { StatusBadge, type StatusTone } from 'components/ui/status-badge';
import { NeedsUpgradeBadge } from 'components/entitlements/NeedsUpgradeBadge';
import { LoadMore } from 'components/ui/pagination';
import { countTestFilters, type TestFilterValue } from 'components/tests/testFilterOptions';
import { dateRangeLabel } from 'lib/dateRange';
import {
  MobileScreen,
  ScreenHeader,
  HeaderIcon,
  HeaderPlus,
  ScreenBody,
  ScrollTabs,
  SearchInput,
  BottomSheet,
  MobileSkeleton,
  MobileEmpty,
  MobileError,
  M,
  scoreColor,
} from 'components/mobile';
import { TestFilterSheet } from './TestFilterSheet';

type Lifecycle = 'all' | 'draft' | 'active' | 'finished';
type Test = TestModule.Types.IEntity.Test;

/** Tests fetched per "load more". */
const PAGE_SIZE = 10;

const STATUS_TONE: Record<Test['status'], StatusTone> = {
  active: 'success',
  published: 'success',
  draft: 'neutral',
  finished: 'done',
  archived: 'neutral',
};

/** Card list with lifecycle tabs, a filter sheet and "load more". Same API params as the desktop `TestList`. */
export function TestListMobile() {
  const { t, i18n } = useTranslation();
  const categoryLabel = useTestCategoryLabel();
  const navigate = useNavigate();
  const { currentOrg } = useOrgStore();
  const {
    state: { organization: authOrg, permissions: p, user },
  } = AuthModule.Context.useAuthContext();
  const orgId = currentOrg?.id ?? authOrg?.id;

  const [lifecycle, setLifecycle] = useState<Lifecycle>('all');
  const [filters, setFilters] = useState<TestFilterValue>({});
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const { input: searchQuery, setInput: setSearchQuery, query: debouncedSearch } = useSearchParamState();
  // Search hides behind the header's icon; a query keeps the field open.
  const [searchOpen, setSearchOpen] = useState(false);
  const showSearch = searchOpen || searchQuery !== '';
  // Per-card actions sheet: null = closed. `confirming` swaps the sheet's body
  // for the delete confirmation rather than stacking a dialog over it.
  const [actionTarget, setActionTarget] = useState<{ id: string; title: string } | null>(null);
  const [confirming, setConfirming] = useState(false);
  const deleteTest = TestModule.Hooks.useDelete();

  const isAuthor = Boolean(p.includes(PERMISSIONS.TESTS_CREATE) || p.includes(PERMISSIONS.TESTS_EDIT));
  const canCreate = p.includes(PERMISSIONS.TESTS_CREATE);
  // AI generate lives in the AI Generate workspace (source documents + chat),
  // not on this screen — only offer it to members who can actually reach it.
  const canAiGenerate = p.includes(PERMISSIONS.DOCUMENTS_EDIT);

  const list = TestModule.Hooks.useInfiniteTests({
    scope: isAuthor ? 'org' : 'available',
    params: {
      search: debouncedSearch || undefined,
      orgId: orgId || undefined,
      status: lifecycle === 'all' ? undefined : lifecycle,
      ...(isAuthor ? filters : {}),
      perPage: PAGE_SIZE,
    },
  });
  const { items: tests, meta, isLoading, isError, refetch } = list;
  const activeFilters = countTestFilters(filters);
  const isFiltering = Boolean(debouncedSearch) || activeFilters > 0 || lifecycle !== 'all';

  const counts = meta.statusCounts;
  const tabs = [
    { value: 'all', label: t('all') },
    { value: 'draft', label: t('draft') },
    { value: 'active', label: t('active') },
    { value: 'finished', label: t('finished') },
  ].map((tab) => {
    const count =
      !counts ? undefined : tab.value === 'all' ? counts.draft + counts.active + counts.finished + counts.archived : counts[tab.value as keyof typeof counts];
    return count == null ? tab : { ...tab, count };
  });

  const goBlankCreate = () => {
    setCreateOpen(false);
    navigate('/app/tests/create');
  };
  const goAiGenerate = () => {
    setCreateOpen(false);
    navigate('/app/documents');
  };
  const openTest = (id: string) =>
    navigate(isAuthor ? `/app/tests/${id}/settings` : `/app/tests/take/${id}`);

  // The API rejects deletes from anyone but the author (403), so mirror that
  // here instead of gating on the permission key alone.
  const canDelete = (test: Test): boolean =>
    p.includes(PERMISSIONS.TESTS_DELETE) && Boolean(test.author?.id) && test.author.id === user?.id;

  const closeActions = () => {
    setActionTarget(null);
    setConfirming(false);
  };

  const handleDelete = async () => {
    if (!actionTarget) return;
    try {
      await deleteTest.mutateAsync({ id: actionTarget.id });
      closeActions();
      refetch();
    } catch {
      /* toast handled by hook */
    }
  };

  const clearAll = () => {
    setSearchQuery('');
    setFilters({});
  };

  const toggleSearch = () => {
    if (showSearch) {
      setSearchQuery('');
      setSearchOpen(false);
    } else {
      setSearchOpen(true);
    }
  };

  // The date filter stays visible on the filter row as "Sana | So'nggi 30 kun ×".
  const dateLabel = dateRangeLabel({ from: filters.createdFrom, to: filters.createdTo }, t, i18n.language);

  const accessLabel = (test: Test): string => {
    if (test.access?.mode === 'marketplace' || isMarketplaceTest(test)) return t('tests_access_marketplace');
    if (test.access?.mode === 'group') return test.access.groupName || t('group');
    if (test.access?.mode === 'code') return test.access.code || t('tests_access_code');
    if (test.access?.mode === 'none') return t('tests_access_none');
    return t('tests_access_public_link');
  };

  const submittedLabel = (test: Test): string | null => {
    if (!test.stats.submitted && test.stats.assigned == null) return null;
    const ratio = test.stats.assigned != null ? `${test.stats.submitted}/${test.stats.assigned}` : `${test.stats.submitted}`;
    return `${ratio} ${t('tests_col_submitted').toLocaleLowerCase()}`;
  };

  return (
    <MobileScreen>
      <ScreenHeader
        title={t('nav.tests')}
        count={meta.total}
        right={
          <>
            <HeaderIcon
              icon={<Search size={19} />}
              label={t('search_assessments')}
              pressed={showSearch}
              onClick={toggleSearch}
            />
            {canCreate && <HeaderPlus onClick={() => setCreateOpen(true)} label={t('create_assessment_2')} />}
          </>
        }
      >
        {showSearch && (
          <div className="pt-3">
            <SearchInput autoFocus value={searchQuery} onChange={setSearchQuery} placeholder={t('search_assessments')} />
          </div>
        )}
        {isAuthor ? (
          <ScrollTabs
            tabs={tabs}
            value={lifecycle}
            onChange={(v) => setLifecycle(v as Lifecycle)}
            aria-label={t('status')}
            className="pt-1"
          />
        ) : (
          <div className="pb-3" />
        )}
      </ScreenHeader>

      {isAuthor && (
        <div className="flex flex-shrink-0 items-center gap-2 px-4 pt-3">
          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            className="inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-[14px] font-medium"
            style={{ background: M.card, border: `1px solid ${M.border}`, color: M.ink }}
          >
            <ListFilter className="h-4 w-4" strokeWidth={1.75} aria-hidden />
            {t('filters')}
            {activeFilters > 0 && (
              <span
                className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[11px] font-semibold"
                style={{ background: M.ink, color: '#FFFFFF' }}
              >
                {activeFilters}
              </span>
            )}
          </button>
          {dateLabel && (
            <span
              className="inline-flex h-9 min-w-0 items-stretch overflow-hidden rounded-md text-[13px]"
              style={{ background: M.card, border: `1px solid ${M.border}` }}
            >
              <button type="button" onClick={() => setFiltersOpen(true)} className="inline-flex min-w-0 items-center gap-2 pl-2.5 pr-2">
                <span style={{ color: M.ink3 }}>{t('date')}</span>
                <span aria-hidden className="h-3.5 w-px flex-shrink-0" style={{ background: M.border }} />
                <span className="truncate font-medium" style={{ color: M.ink }}>
                  {dateLabel}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setFilters({ ...filters, createdFrom: undefined, createdTo: undefined })}
                aria-label={t('filter_remove', { label: t('date') })}
                className="inline-flex w-8 flex-shrink-0 items-center justify-center"
                style={{ borderLeft: `1px solid ${M.border}`, color: M.ink3 }}
              >
                <X size={14} aria-hidden />
              </button>
            </span>
          )}
        </div>
      )}

      {isLoading ? (
        <MobileSkeleton />
      ) : isError ? (
        <MobileError onRetry={() => refetch()} />
      ) : tests.length === 0 ? (
        <MobileEmpty
          icon={isFiltering ? <Search size={22} /> : <FileText size={22} />}
          title={isFiltering ? t('no_results') : t('no_assessments_yet')}
          description={
            isFiltering
              ? t('try_adjusting_your_search_or_filters')
              : canCreate
                ? t('create_first_assessment_to_start_evaluating')
                : t('no_assessments_available_yet')
          }
          action={
            (activeFilters > 0 || searchQuery) ? (
              <button
                type="button"
                onClick={clearAll}
                className="h-10 rounded-md px-4 text-[14px] font-medium"
                style={{ background: M.card, border: `1px solid ${M.border}`, color: M.ink }}
              >
                {t('clear_filters')}
              </button>
            ) : undefined
          }
        />
      ) : (
        <ScreenBody>
          <div className="overflow-hidden rounded-lg" style={{ background: M.card, border: `1px solid ${M.border}` }}>
            {tests.map((test, index) => {
              const avg = test.stats.avgScore > 0 ? Math.round(test.stats.avgScore) : null;
              const submitted = submittedLabel(test);
              return (
                <div
                  key={test.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openTest(test.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openTest(test.id);
                    }
                  }}
                  className="flex cursor-pointer flex-col gap-2 px-3.5 py-3"
                  style={{ borderTop: index === 0 ? 'none' : `1px solid ${M.border}` }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className="min-w-0 break-words text-[15px] font-medium leading-snug" style={{ color: M.ink }}>
                      {test.title}
                    </p>
                    <div className="flex flex-shrink-0 items-center gap-1">
                      {isAuthor && (
                        <span className="text-[15px] font-bold tabular-nums" style={{ color: avg == null ? M.faint : scoreColor(avg) }}>
                          {avg == null ? '—' : `${avg}%`}
                        </span>
                      )}
                      {canDelete(test) && (
                        // -mr/-mt pull the 32px tap target back to the card's
                        // optical edge without shrinking it below the touch min.
                        <button
                          type="button"
                          aria-label={t('more_options')}
                          onClick={(e) => {
                            e.stopPropagation();
                            setActionTarget({ id: test.id, title: test.title });
                          }}
                          className="-mr-1.5 -mt-1 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md"
                          style={{ color: M.ink3 }}
                        >
                          <MoreVertical size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px]" style={{ color: M.ink3 }}>
                    {isAuthor && <StatusBadge tone={STATUS_TONE[test.status] ?? 'neutral'}>{t(test.status)}</StatusBadge>}
                    {isAuthor && test.needsUpgrade && (
                      <NeedsUpgradeBadge
                        featureKey={test.needsUpgrade.featureKey}
                        questionCount={test.needsUpgrade.questionCount}
                      />
                    )}
                    <span className="tabular-nums">{t('n_questions', { count: test.totalQuestions })}</span>
                    {categoryLabel(test.category) && <span className="min-w-0 truncate">· {categoryLabel(test.category)}</span>}
                    {isAuthor && submitted && <span className="tabular-nums">· {submitted}</span>}
                    {isAuthor && <span className="min-w-0 truncate">· {accessLabel(test)}</span>}
                  </div>
                </div>
              );
            })}
          </div>
          <LoadMore
            compact
            className="mt-4"
            shown={tests.length}
            total={meta.total}
            step={PAGE_SIZE}
            onLoadMore={() => list.fetchNextPage()}
            loading={list.isFetchingNextPage}
          />
        </ScreenBody>
      )}

      {isAuthor && (
        <TestFilterSheet
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          orgId={orgId}
          currentUserId={user?.id}
          value={filters}
          onChange={setFilters}
          total={meta.total}
        />
      )}

      {/* Per-card actions. Tapping the card body opens the test, so this sheet
          carries only what a tap can't reach — currently the destructive one,
          behind an in-sheet confirm step. */}
      <BottomSheet open={Boolean(actionTarget)} onClose={closeActions}>
        <p className="mb-3.5 truncate text-[15px] font-bold" style={{ color: M.ink }}>
          {actionTarget?.title}
        </p>
        {confirming ? (
          <>
            <p className="mb-3.5 text-[12.5px]" style={{ color: M.ink3 }}>
              {t('trash.test_delete_description')}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={closeActions}
                disabled={deleteTest.isPending}
                className="flex-1 rounded-md py-3 text-[13px] font-semibold disabled:opacity-60"
                style={{ border: `1px solid ${M.border}`, color: M.ink2 }}
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteTest.isPending}
                className="flex-1 rounded-md py-3 text-[13px] font-bold text-white disabled:opacity-60"
                style={{ background: M.redBright }}
              >
                {t('delete')}
              </button>
            </div>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="flex w-full items-center gap-2.5 rounded-md p-3 text-left"
            style={{ border: `1px solid ${M.red2}`, background: M.redTint }}
          >
            <Trash2 className="h-4 w-4 flex-shrink-0" style={{ color: M.redBright }} />
            <p className="text-[12.5px] font-bold" style={{ color: M.red }}>
              {t('delete_assessment')}
            </p>
          </button>
        )}
      </BottomSheet>

      {/* Each option below is a real, distinct destination — no shared
          "confirm" step, since tapping a card already navigates. */}
      <BottomSheet open={createOpen} onClose={() => setCreateOpen(false)}>
        <p className="mb-3.5 text-[15px] font-bold" style={{ color: M.ink }}>
          {t('create_assessment_2')}
        </p>
        <div className="flex flex-col gap-2">
          {canAiGenerate && (
            <button
              type="button"
              onClick={goAiGenerate}
              className="flex items-center gap-3 rounded-md p-3 text-left"
              style={{ border: `1px solid ${M.border}` }}
            >
              <Sparkles className="h-[18px] w-[18px] flex-shrink-0" strokeWidth={1.75} style={{ color: M.ink2 }} />
              <div>
                <p className="text-[13.5px] font-semibold" style={{ color: M.ink }}>
                  {t('ai_generate', 'AI generate')}
                </p>
                <p className="text-[12px]" style={{ color: M.ink3 }}>
                  {t('from_a_document_or_prompt', 'From a document or prompt')}
                </p>
              </div>
            </button>
          )}
          <button
            type="button"
            onClick={goBlankCreate}
            className="flex items-center gap-3 rounded-md p-3 text-left"
            style={{ border: `1px solid ${M.border}` }}
          >
            <Plus className="h-[18px] w-[18px] flex-shrink-0" strokeWidth={1.75} style={{ color: M.ink2 }} />
            <div>
              <p className="text-[13.5px] font-semibold" style={{ color: M.ink }}>
                {t('blank_test', 'Blank test')}
              </p>
              <p className="text-[12px]" style={{ color: M.ink3 }}>
                {t('start_from_scratch', 'Start from scratch')}
              </p>
            </div>
          </button>
        </div>
      </BottomSheet>
    </MobileScreen>
  );
}

export default TestListMobile;
